import moment from "moment";
import { PaymentProcessor } from "../../../Account/BillingInformation/Shared/types/PaymentProcessorTypes";
import { AdjustmentValidation } from "../../../MealTrack/MTAPITypes";
import { getSelectedIngredientOptionTotal } from "../../../OrderFlow/hooks/useCalculations";
import { ERROR_MESSAGE_GENERIC, ORDER_STATUS } from "../../../Shared/utils/constants";
import deDupeWarningText from "../../utils/deDupeWarningText";
import { proceedWithSaveChanges } from "../../utils/proceedWithSaveChanges";
import { updateOrder } from "../../utils/requests";
const buildDaySummary = (days, getRecipesForMeal, displayRecipesByRecipeType, selectedOrderRecipeType) => {
    return days
        .filter((day) => day.Meals.some((meal) => meal.Quantity > 0) || day.Products.some((product) => product.NewQuantity > 0))
        .map((day) => {
        return {
            date: moment(day.DateFormatted).format("YYYY-MM-DD"),
            meals: day.Meals.filter((meal) => meal.Quantity > 0).map((meal) => {
                const recipes = getRecipesForMeal(day.Recipes, meal, displayRecipesByRecipeType, selectedOrderRecipeType);
                return {
                    name: meal.Name,
                    programMealId: meal.ProgramMealId,
                    quantity: meal.Quantity,
                    price: meal.Price,
                    recipes: recipes.flatMap((recipe) => recipe.Assignments.map((assignment) => {
                        var _a, _b;
                        const replacementPrice = getSelectedIngredientOptionTotal((_a = assignment.SelectedIngredientOptions) === null || _a === void 0 ? void 0 : _a.filter((i) => i.mustBeReplaced));
                        return {
                            assignmentId: assignment.AssignmentId,
                            hasMustReplaceIngredients: recipe.RecipeIngredients.filter((i) => i.MustReplace).length > 0,
                            name: assignment.RecipeName,
                            price: (_b = assignment.RecipeAssignmentPrice) !== null && _b !== void 0 ? _b : 0,
                            quantity: assignment.Quantity,
                            recipeId: assignment.RecipeId,
                            replacementPrice: replacementPrice,
                            selectedIngredientOptions: assignment.SelectedIngredientOptions,
                            shortName: recipe.RecipeShortName,
                        };
                    })),
                };
            }),
            products: day.Products.filter((p) => p.NewQuantity > 0).map((product) => {
                return {
                    name: product.ProductName,
                    quantity: product.NewQuantity,
                    id: product.ProductId,
                    price: product.ProductPrice,
                    sale: product.ProductSalePrice,
                    dayOfWeek: moment(product.MealDateFormatted).day(),
                    isOnShortWeek: false,
                };
            }),
            isDayPastCutoff: day.IsDayPastCutOff,
        };
    });
};
export const buildOrderSummary = (days, orderId, programName, subscriptionDiscount, getRecipesForMeal, myMealsConfig, selectedOrderRecipeType, balance, sku, isFutureOrder, questions) => {
    var _a, _b, _c, _d, _e, _f;
    return {
        // Without this the skeleton shows indefinitely
        orderId: orderId,
        balance: balance,
        planName: programName,
        subscriptionPeriod: 1,
        omissions: [],
        totalDayBankValue: 0,
        appliedDayBanks: [],
        dates: buildDaySummary(days, getRecipesForMeal, myMealsConfig.DisplayRecipesByRecipeType, selectedOrderRecipeType),
        discounts: {
            subscription: (balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalSubscriptionDiscount) &&
                (balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalSubscriptionDiscount) > 0
                ? {
                    value: subscriptionDiscount,
                    appliedValue: balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalSubscriptionDiscount,
                }
                : undefined,
            coupons: balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustments.filter((adjustment) => adjustment.validation === AdjustmentValidation.Valid).map((adjustment) => {
                return {
                    name: adjustment.name,
                    type: adjustment.application,
                    value: adjustment.adjustmentValue,
                    appliedValue: adjustment.appliedValue,
                    adjustmentType: adjustment.adjustmentType,
                    validationMsg: adjustment.validation,
                    automaticDiscount: adjustment.automatic,
                };
            }),
            giftCard: (balance === null || balance === void 0 ? void 0 : balance.giftCardPayments) && (balance === null || balance === void 0 ? void 0 : balance.giftCardPayments.length) > 0
                ? {
                    giftCardAmount: balance === null || balance === void 0 ? void 0 : balance.giftCardPayments[0].appliedValue,
                    giftCardNumber: balance === null || balance === void 0 ? void 0 : balance.giftCardPayments[0].giftCardCode,
                }
                : undefined,
        },
        tax: (_a = balance === null || balance === void 0 ? void 0 : balance.cost.order.totalTax) !== null && _a !== void 0 ? _a : 0,
        taxRate: (_b = balance === null || balance === void 0 ? void 0 : balance.cost.order.taxRate) !== null && _b !== void 0 ? _b : 0,
        shipping: (_c = balance === null || balance === void 0 ? void 0 : balance.cost.order.totalShipping) !== null && _c !== void 0 ? _c : 0,
        subtotal: (_d = balance === null || balance === void 0 ? void 0 : balance.cost.order.subtotal) !== null && _d !== void 0 ? _d : 0,
        total: (_e = balance === null || balance === void 0 ? void 0 : balance.remainingBalance) !== null && _e !== void 0 ? _e : 0,
        fullDayDiscount: (_f = balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalFullDayDiscount) !== null && _f !== void 0 ? _f : 0,
        subscriptions: undefined,
        isSubscription: true,
        sku: sku,
        isFutureOrder: isFutureOrder,
        questions: questions,
    };
};
export const saveAndUpdateFutureOrderPrehook = (checkSubscriptionMealsPrehook, saveAndUpdateFutureOrder) => {
    if (checkSubscriptionMealsPrehook) {
        checkSubscriptionMealsPrehook(() => saveAndUpdateFutureOrder());
    }
    else {
        saveAndUpdateFutureOrder();
    }
};
export const onSaveClick = (checkSubscriptionMealsPrehook, recipeIngredientReplacementMode, balance, myMealsConfig, days, totalMealQuantityPerDay, selectedOrderMealQuantity, amountsPerDay, amountForOrder, totalQuantitiesPerDay, orderType, haveQuantitiesChanged, skipPayment, orderStatus, orderId, handleShowErrorModal, setIsPaymentDialogOpen, setIsLoading, onComplete, toast) => {
    if (checkSubscriptionMealsPrehook) {
        checkSubscriptionMealsPrehook(() => saveChanges(myMealsConfig, recipeIngredientReplacementMode, balance, days, totalMealQuantityPerDay, selectedOrderMealQuantity, amountsPerDay, amountForOrder, totalQuantitiesPerDay, orderType, orderId, haveQuantitiesChanged, skipPayment, orderStatus, handleShowErrorModal, setIsPaymentDialogOpen, setIsLoading, onComplete, toast));
    }
    else {
        saveChanges(myMealsConfig, recipeIngredientReplacementMode, balance, days, totalMealQuantityPerDay, selectedOrderMealQuantity, amountsPerDay, amountForOrder, totalQuantitiesPerDay, orderType, orderId, haveQuantitiesChanged, skipPayment, orderStatus, handleShowErrorModal, setIsPaymentDialogOpen, setIsLoading, onComplete, toast);
    }
};
const saveChanges = (myMealsConfig, recipeIngredientReplacementMode, balance, days, totalMealQuantityPerDay, selectedOrderMealQuantity, amountsPerDay, amountForOrder, totalQuantitiesPerDay, orderType, orderId, haveQuantitiesChanged, skipPayment, orderStatus, handleShowErrorModal, setIsPaymentDialogOpen, setIsLoading, onComplete, toast) => {
    const saveChangesStatus = proceedWithSaveChanges(myMealsConfig, days, totalMealQuantityPerDay, selectedOrderMealQuantity, amountsPerDay, amountForOrder, totalQuantitiesPerDay, orderType);
    if (!saveChangesStatus.proceed) {
        const deDupedWarningText = deDupeWarningText(saveChangesStatus.dayWarningMessages);
        handleShowErrorModal(deDupedWarningText);
        return;
    }
    if (haveQuantitiesChanged && !skipPayment && orderStatus !== ORDER_STATUS.PENDING && balance.remainingBalance > 0) {
        setIsPaymentDialogOpen(true);
        return;
    }
    setIsLoading(true);
    updateOrder(days, orderId, false, undefined, recipeIngredientReplacementMode, PaymentProcessor.UNKNOWN, undefined, onSuccess(setIsLoading, toast, onComplete), onError(setIsLoading, toast), undefined);
};
const onSuccess = (setIsLoading, toast, onComplete) => (res) => {
    var _a, _b, _c;
    if (!((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.success)) {
        setIsLoading(false);
        toast.push((_c = (_b = res === null || res === void 0 ? void 0 : res.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : ERROR_MESSAGE_GENERIC, { variant: "error" });
    }
    else {
        onComplete();
    }
};
const onError = (setIsLoading, toast) => (e) => {
    var _a;
    setIsLoading(false);
    toast.push((_a = e === null || e === void 0 ? void 0 : e.message) !== null && _a !== void 0 ? _a : ERROR_MESSAGE_GENERIC, { variant: "error" });
};
// Helper function to format dates for getOrderDisplayDates
const formatDate = (dateStr) => dateStr && moment(dateStr).isValid() ? moment(dateStr).format("dddd MMM Do") : "";
// Constant message to be used when dates are not available
const DATES_NOT_AVAILABLE = "Dates are not currently available.";
/**
 *
 * @param days - Array of MyMealsDayType objects
 * @returns a formatted string representing the order display dates
 *          or a message indicating that dates are not available.
 */
export const getOrderDisplayDates = (days) => {
    // Check if the days array is empty or not.
    // If it's empty, return a message indicating that dates are not available.
    var _a, _b;
    if (!Array.isArray(days) || days.length === 0) {
        return DATES_NOT_AVAILABLE;
    }
    // Check if the first and last elements of the days array are valid.
    const firstDate = (_a = days[0]) === null || _a === void 0 ? void 0 : _a.DateFormatted;
    const lastDate = (_b = days.at(-1)) === null || _b === void 0 ? void 0 : _b.DateFormatted;
    // If either of the dates is invalid, return a message indicating that dates are not available.
    const orderStartDate = formatDate(firstDate);
    const orderEndDate = formatDate(lastDate);
    // If the start and end dates are the same, return just the start date.
    if (orderStartDate && orderEndDate) {
        if (orderStartDate === orderEndDate)
            return orderStartDate;
        else
            return `${orderStartDate} - ${orderEndDate}`;
    }
    // If only one of the dates is valid, return that date.
    if (orderStartDate && !orderEndDate)
        return orderStartDate;
    if (!orderStartDate && orderEndDate)
        return orderEndDate;
    // If both dates are invalid, return a message indicating that dates are not available.
    else
        return DATES_NOT_AVAILABLE;
};
